import { NextSeo } from 'next-seo';
import { getCanonicalPath } from '@/lib/algolia/getCanonicalPath';
import { ProductPageJsonLd } from './components/JsonLd';
import { ProductPageData } from './getData';
import Debug from '@/components/debug';
import { siteUrl } from '@/next-sitemap.config';
import ProductGallery from '@/cataloguePages/productPage/components/ProductGallery';
import { Button, ProductTags, Promotion } from '@fagmobler/ui';
import { formatServerTags } from '@/lib/algolia/tags';
import clsx from 'clsx';
import {
  KeyProperties,
  SizeProperties,
} from '@/cataloguePages/productPage/components/properties';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import Link from 'next/link';
import { fagmoblerSlugify } from '@/lib/utils/getUrls';
import { ProductPageAccordion } from '@/cataloguePages/productPage/components/Accordion';
import { useEffect, useRef, useState } from 'react';
import { FagmoblerPortableText } from '@/components/portableText';
import { ProductPagePdfs } from '@/cataloguePages/productPage/components/Pdfs';
import ProductsInSerie from '@/cataloguePages/productPage/components/ProductsInSerie';
import RelatedProducts from '@/cataloguePages/productPage/components/RelatedProducts';
import { SerieInfo } from '@/cataloguePages/productPage/components/SerieInfo';
import AddToCartBlock from '@/cataloguePages/productPage/components/AddToCartBlock';
import ProductPrice from '@/cataloguePages/productPage/components/ProductPrice';
import { useRouter } from 'next/router';
import { EcommerceObject, trackEvent } from '@/lib/analytics';
import { useStores } from '@/components/store';

type CommonProps = {
  preview: boolean;
  previewData: any | null;
  slugs: string[] | null;
};

export type ProductPageProps = ProductPageData & CommonProps;

export function ProductPage({
  product,
  omniumProduct,
  algoliaProduct,
  productsInSerie,
  motherProductSKU,
  breadcrumbs,
  productEnrichment,
  serieEnrichment,
  previewData,
  preview = false,
}: ProductPageProps) {
  const { query } = useRouter();
  const productEnrichmentRef = useRef<HTMLDivElement>(null);
  const [enrichmentOpen, setEnrichmentOpen] = useState(false);
  const { current: currentStoreQuery } = useStores();
  const currentStore = currentStoreQuery.data?.store;

  if (!product || !algoliaProduct || !omniumProduct) return null;

  const canonicalPath = getCanonicalPath(algoliaProduct);
  const canonicalUrl = canonicalPath && `${siteUrl}${canonicalPath}`;
  const tagKeys = product.tags?.map((tag) => tag.key) || [];
  const serie = product.properties?.find((p) => p.key === 'serie');
  const relatedProducts =
    product.relatedProducts?.find((p) => p.key === 'publisertSammen')
      ?.relatedProductItems || [];

  const storeIsFetched = currentStoreQuery.isFetched;

  const slugs = query.slugs as string[];
  const path = slugs.join('/');

  useEffect(() => {
    const ecommerceObject: EcommerceObject = {
      value: omniumProduct.price.unitPrice || undefined,
      currency: 'NOK',
      items: [
        {
          item_id: omniumProduct.skuId,
          price: omniumProduct.price.unitPrice || undefined,
          quantity: 1,
        },
      ],
    };
    if (!ecommerceObject || !path || !storeIsFetched) return;
    trackEvent('view_item', ecommerceObject, currentStore || undefined);
    // We need to track only when the path changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, storeIsFetched]);

  return (
    <>
      <ProductPageJsonLd
        algoliaProduct={algoliaProduct}
        product={product}
        canonicalUrl={canonicalUrl}
      />
      <NextSeo
        title={product.posterName}
        canonical={canonicalUrl}
        description={product.posterDescription}
        openGraph={{
          type: 'website',
          url: canonicalUrl,
          title: product.posterName,
          description: product.posterDescription,
          images: [
            {
              url: product.media?.[0]?.url,
              width: 800,
              height: 600,
              alt: product.posterName,
            },
          ],
        }}
      />

      <div className="md:grid md:grid-cols-12 gap-8 mt-5 print:!block">
        <ProductGallery product={product}>
          {product.price.discountPercent &&
            product.price.discountPercent > 0 && (
              <Promotion
                size="lg"
                className="absolute bottom-32 right-5 md:right-8 lg:right-10 z-10"
                percent={product.price.discountPercent}
                innerClassName={
                  tagKeys.includes('BLACK WEEK')
                    ? 'bg-[#F4A933] text-neutral-80'
                    : ''
                }
              />
            )}
        </ProductGallery>

        <div className="mt-5 md:mt-0 md:col-span-6">
          {product.tags && (
            <ProductTags
              tags={formatServerTags(product.tags, 4)}
              variant="pdp"
            />
          )}

          <h1 className="hd-xl print:hd-3xl print:mt-0">
            {product.posterName}
          </h1>
          <p className="mb-4 print:hd-base">{product.posterDescription}</p>

          {product.texts && (
            <div className="mb-5 leading-snug">
              {product.texts.shortdescription}
            </div>
          )}

          {product.texts?.salesinfo && (
            <div className="mb-5 leading-snug">{product.texts.salesinfo}</div>
          )}

          {productEnrichment && productEnrichment?.body?.length > 0 && (
            <a
              href="#produktbeskrivelse"
              onClick={(event) => {
                if (productEnrichmentRef.current && window) {
                  setEnrichmentOpen(true);
                  const headerOffset = 100;
                  const elementPosition =
                    productEnrichmentRef.current.getBoundingClientRect().top;
                  const offsetPosition = elementPosition - headerOffset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                  });
                  event.preventDefault();
                  return false;
                }
              }}
              className="block mb-5 underline underline-offset-2"
            >
              Les mer om produktet
            </a>
          )}

          {serie && (
            <Link href={`/serie/${fagmoblerSlugify(serie.value as string)}`}>
              <Button
                Icon={ArrowRightIcon}
                iconPlacement="right"
                variant="outline"
                className="print:hidden"
              >
                Vis alle fra {serie.value} serien
              </Button>
            </Link>
          )}

          <div className={clsx('my-4')}>
            <SizeProperties product={product} />
          </div>

          {relatedProducts.length > 0 && (
            <RelatedProducts
              products={[
                ...relatedProducts,
                {
                  productId: product.sku,
                  posterName: product.posterName,
                  posterDescription: product.posterDescription,
                  image: product.media?.[0]?.url,
                  price: product.price,
                },
              ]}
              currentId={product.sku}
              basePath={breadcrumbs?.at(motherProductSKU ? -2 : -1)?.path}
            />
          )}

          <div className={clsx('mb-8')}>
            <ProductPrice price={product.price} />
          </div>
          <AddToCartBlock
            product={product}
            algoliaProduct={algoliaProduct}
            omniumProduct={omniumProduct}
          />
        </div>
      </div>

      <div className="space-y-10 mt-10">
        <div className="space-y-2">
          <ProductPageAccordion title="Egenskaper">
            <KeyProperties product={product} />
          </ProductPageAccordion>
          <div className="print:hidden">
            {product.pdfs && product.pdfs.length > 0 && (
              <ProductPageAccordion title="Kataloger">
                <ProductPagePdfs pdfs={product.pdfs} />
              </ProductPageAccordion>
            )}
          </div>
          {serie &&
            serieEnrichment &&
            serieEnrichment.body &&
            serieEnrichment.body.length > 0 && (
              <div className="print:hidden">
                <ProductPageAccordion title={`Om serien ${serie.value}`}>
                  <SerieInfo serie={serieEnrichment} />
                </ProductPageAccordion>
              </div>
            )}

          <div
            className="print:hidden"
            ref={productEnrichmentRef}
            id="produktbeskrivelse"
          >
            {productEnrichment?.body &&
              productEnrichment.body.length > 0 && ( //TODO: Fix images not showing up
                <ProductPageAccordion
                  title="Produktbeskrivelse"
                  isOpen={enrichmentOpen}
                >
                  <FagmoblerPortableText value={productEnrichment.body} />
                </ProductPageAccordion>
              )}
          </div>
        </div>
        {serie && productsInSerie && productsInSerie.length > 2 && (
          <ProductsInSerie products={productsInSerie} serie={serie} />
        )}
      </div>

      <div className="print:hidden">
        <Debug data={product} label="Product" />
        <Debug data={algoliaProduct} label="algoliaProduct" />
      </div>
    </>
  );
}

export default ProductPage;
